import { useRef, useState } from 'react';
import {
  createBroadcast,
  getBroadcasts,
  type BroadcastAudience,
  type BroadcastFilter,
  type BroadcastMediaType,
  type CreateBroadcastInput,
  type UserStatus,
} from '../api';
import { badgeClass } from '../format';
import { useApi } from '../useApi';

/** Segment sub-options driven by the `premium`/`status` filter knobs. */
type SegmentChoice = 'all' | 'premium' | 'free' | 'active' | 'suspended' | 'banned';

function segmentFilter(choice: SegmentChoice): BroadcastFilter {
  switch (choice) {
    case 'premium':
      return { premium: true };
    case 'free':
      return { premium: false };
    case 'active':
    case 'suspended':
    case 'banned':
      return { status: choice as UserStatus };
    default:
      return {}; // 'all' within segment = no extra constraint (every DM-allowed user)
  }
}

/** Split a comma/space/newline separated list into trimmed, non-empty tokens. */
function tokenize(raw: string): string[] {
  return raw
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter(Boolean);
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function ComposeSection({ onSent }: { onSent: () => void }) {
  const [audience, setAudience] = useState<BroadcastAudience>('all');
  const [segment, setSegment] = useState<SegmentChoice>('all');
  const [recipientsRaw, setRecipientsRaw] = useState(''); // usernames/ids for 'users'
  const [bodyHtml, setBodyHtml] = useState('');
  const [mediaType, setMediaType] = useState<'none' | BroadcastMediaType>('none');
  const [mediaUrl, setMediaUrl] = useState('');
  const [buttonLabel, setButtonLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  /** Wrap the current textarea selection in a Telegram-HTML tag. */
  const wrap = (open: string, close: string) => {
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart ?? bodyHtml.length;
    const end = el.selectionEnd ?? bodyHtml.length;
    const selected = bodyHtml.slice(start, end) || 'text';
    const next = bodyHtml.slice(0, start) + open + selected + close + bodyHtml.slice(end);
    setBodyHtml(next);
    // Restore focus + place caret after the inserted markup.
    requestAnimationFrame(() => {
      el.focus();
      const caret = start + open.length + selected.length + close.length;
      el.setSelectionRange(caret, caret);
    });
  };

  const submit = async () => {
    setError(null);
    setResult(null);

    const input: CreateBroadcastInput = { audience };
    if (bodyHtml.trim()) input.bodyHtml = bodyHtml.trim();
    if (mediaType !== 'none') {
      if (!mediaUrl.trim()) {
        setError('Enter a media URL or set media to "none".');
        return;
      }
      input.mediaType = mediaType;
      input.mediaUrl = mediaUrl.trim();
    }
    if (buttonLabel.trim()) input.buttonLabel = buttonLabel.trim();

    if (audience === 'segment') {
      input.filter = segmentFilter(segment);
    } else if (audience === 'users') {
      const tokens = tokenize(recipientsRaw);
      if (tokens.length === 0) {
        setError('Enter at least one username or user id.');
        return;
      }
      const userIds = tokens.filter((t) => UUID_RE.test(t));
      const usernames = tokens.filter((t) => !UUID_RE.test(t)).map((t) => t.replace(/^@/, ''));
      input.filter = {
        ...(userIds.length ? { userIds } : {}),
        ...(usernames.length ? { usernames } : {}),
      };
    }

    if (!input.bodyHtml && !input.mediaUrl) {
      setError('Add a message body or media.');
      return;
    }

    const audienceLabel =
      audience === 'all'
        ? 'ALL users who allow bot DMs'
        : audience === 'segment'
          ? `the "${segment}" segment`
          : `${tokenize(recipientsRaw).length} specific recipient(s)`;
    if (!window.confirm(`Send this broadcast to ${audienceLabel}? This cannot be undone.`)) return;

    setBusy(true);
    try {
      const res = await createBroadcast(input);
      setResult(
        `Broadcast created — ${res.enqueued} queued, ${res.failed} failed (total ${res.totalCount}). Status: ${res.status}.`,
      );
      setBodyHtml('');
      setMediaUrl('');
      setRecipientsRaw('');
      setButtonLabel('');
      onSent();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Send failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="card-head">
        <span className="card-title">Compose</span>
      </div>

      {error && <p className="error-line">{error}</p>}
      {result && (
        <p style={{ color: 'var(--accent, #f97316)', fontSize: '0.85rem', marginTop: 0 }}>{result}</p>
      )}

      {/* Audience */}
      <div className="form-row">
        <div className="field" style={{ minWidth: 200 }}>
          <label>Audience</label>
          <select
            className="input"
            value={audience}
            onChange={(e) => setAudience(e.target.value as BroadcastAudience)}
          >
            <option value="all">All users</option>
            <option value="segment">Segment</option>
            <option value="users">Specific users</option>
          </select>
        </div>

        {audience === 'segment' && (
          <div className="field" style={{ minWidth: 200 }}>
            <label>Segment</label>
            <select
              className="input"
              value={segment}
              onChange={(e) => setSegment(e.target.value as SegmentChoice)}
            >
              <option value="all">All (DM-allowed)</option>
              <option value="premium">Premium only</option>
              <option value="free">Free only</option>
              <option value="active">Status: active</option>
              <option value="suspended">Status: suspended</option>
              <option value="banned">Status: banned</option>
            </select>
          </div>
        )}
      </div>

      {audience === 'users' && (
        <div className="field" style={{ marginTop: 14 }}>
          <label>Recipients</label>
          <textarea
            className="input"
            rows={2}
            placeholder="Usernames or user ids — comma, space or newline separated (e.g. @alice, bob, 0190…-uuid)"
            value={recipientsRaw}
            onChange={(e) => setRecipientsRaw(e.target.value)}
          />
        </div>
      )}

      {/* Body + quick HTML buttons */}
      <div className="field" style={{ marginTop: 14 }}>
        <label>Message — Telegram HTML</label>
        <div className="toolbar" style={{ marginBottom: 6 }}>
          <button type="button" className="btn" onClick={() => wrap('<b>', '</b>')}>
            Bold
          </button>
          <button type="button" className="btn" onClick={() => wrap('<i>', '</i>')}>
            Italic
          </button>
          <button type="button" className="btn" onClick={() => wrap('<u>', '</u>')}>
            Underline
          </button>
          <button type="button" className="btn" onClick={() => wrap('<a href="https://">', '</a>')}>
            Link
          </button>
          <button type="button" className="btn" onClick={() => wrap('<code>', '</code>')}>
            Code
          </button>
        </div>
        <textarea
          ref={bodyRef}
          className="input"
          rows={6}
          placeholder="Message body — Telegram HTML…"
          value={bodyHtml}
          onChange={(e) => setBodyHtml(e.target.value)}
        />
        <span className="hint">
          Telegram HTML only: <code>&lt;b&gt; &lt;i&gt; &lt;u&gt; &lt;s&gt; &lt;a href&gt; &lt;code&gt; &lt;pre&gt;</code>. With media, the body is the caption (max 1024 chars).
        </span>
      </div>

      {/* Media + button label */}
      <div className="form-row" style={{ marginTop: 14 }}>
        <div className="field" style={{ minWidth: 160 }}>
          <label>Media</label>
          <select
            className="input"
            value={mediaType}
            onChange={(e) => setMediaType(e.target.value as 'none' | BroadcastMediaType)}
          >
            <option value="none">None</option>
            <option value="photo">Photo</option>
            <option value="video">Video</option>
          </select>
        </div>
        {mediaType !== 'none' && (
          <div className="field" style={{ flex: 1, minWidth: 280 }}>
            <label>Media URL</label>
            <input
              className="input"
              placeholder="Public media URL (https://…)"
              value={mediaUrl}
              onChange={(e) => setMediaUrl(e.target.value)}
            />
          </div>
        )}
        <div className="field" style={{ minWidth: 200 }}>
          <label>Button label</label>
          <input
            className="input"
            placeholder="(default open-app label)"
            value={buttonLabel}
            onChange={(e) => setButtonLabel(e.target.value)}
          />
        </div>
      </div>

      <div className="toolbar" style={{ marginTop: 16 }}>
        <button className="btn btn-primary" disabled={busy} onClick={() => void submit()}>
          {busy ? 'Sending…' : 'Send broadcast'}
        </button>
      </div>
    </div>
  );
}

function HistorySection({ reloadKey }: { reloadKey: number }) {
  const { data, error, loading } = useApi(getBroadcasts, [reloadKey]);
  const rows = data?.data ?? [];

  return (
    <div className="card">
      <div className="card-head">
        <span className="card-title">Recent broadcasts</span>
        {data && <span className="muted">{rows.length} broadcasts</span>}
      </div>
      <div className="table-wrap">
        {error ? (
          <div className="empty">{error}</div>
        ) : loading && !data ? (
          <div className="empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="empty">No broadcasts yet.</div>
        ) : (
          <table className="flm">
            <thead>
              <tr>
                <th>When</th>
                <th>Audience</th>
                <th>Status</th>
                <th>Sent</th>
                <th>Failed</th>
                <th>Total</th>
                <th>Body</th>
                <th>Media</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.id}>
                  <td className="muted">{new Date(b.createdAt).toLocaleString()}</td>
                  <td>{b.audience}</td>
                  <td>
                    <span className={badgeClass(b.status)}>{b.status}</span>
                  </td>
                  <td className="num">{b.sentCount}</td>
                  <td className="num">{b.failedCount}</td>
                  <td className="num">{b.totalCount}</td>
                  <td className="muted" title={b.bodyHtml} style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {b.bodyHtml}
                  </td>
                  <td className="muted">{b.mediaType ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function Broadcast() {
  const [reloadKey, setReloadKey] = useState(0);

  return (
    <div className="admin-wrap">
      <div className="page-head">
        <h1>
          Broadcast<span className="dot">.</span>
        </h1>
      </div>
      <ComposeSection onSent={() => setReloadKey((k) => k + 1)} />
      <HistorySection reloadKey={reloadKey} />
    </div>
  );
}
